import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import Sortable from "sortablejs";
import {
  CloseIcon,
  DeleteIcon
} from "../../front-end-global-components/assets/assets";
import Button from "../../front-end-global-components/components/Button/Button";
import Modal from "../../front-end-global-components/components/Modal/Modal";
import PDFRenderer from "../../front-end-global-components/components/PDFRenderer/PDFRenderer";
import { MAX_PDF_TOTAL_BYTES, MAX_UPLOAD_FILES } from "../../utils/pdf";
import "./ReArrangeFiles.css";

// The image variant keeps the selectors of the grid it replaced.
const DATA_CY = {
  image: {
    root: "REARRANGE-AND-ADD-PRESCRIPTION-COMP",
    add: "ADD-IMAGE-CARD-ELEMENT",
    upload: "ARRANGE-ADD-PRESCRIPTION-BTN"
  },
  pdf: {
    root: "PDF-REARRANGE-COMP",
    add: "ADD-PDF-CARD",
    upload: "PDF-UPLOAD-BUTTON"
  }
};

// Drag-to-reorder grid for one upload batch. `items` are
// { id, file, thumbnail, pageCount? }, built by the file picker; `kind` is
// "image" or "pdf". Leaving the grid (uploaded or backed out) ends the batch.
export default function ReArrangeFiles(props) {
  const listRef = useRef(null);
  // Sortable is created once; this keeps its callbacks on the latest setter.
  const setItemsRef = useRef(props.setItems);
  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    setItemsRef.current = props.setItems;
  });

  useEffect(() => {
    if (!listRef.current) return;

    const sortable = Sortable.create(listRef.current, {
      draggable: ".allow-dragging",
      onMove: (event) =>
        !event.related.classList.contains("rearrange-add-card"),
      onEnd: ({ item, from, oldIndex, newIndex }) => {
        // Undo Sortable's DOM move so React stays the owner of the list,
        // then reorder through state.
        from.removeChild(item);
        from.insertBefore(item, from.children[oldIndex] || null);

        // an updater, so back-to-back drags each see the latest order
        setItemsRef.current((previous) => {
          const items = [...previous];
          const target = Math.min(newIndex, items.length - 1);
          const [moved] = items.splice(oldIndex, 1);
          items.splice(target, 0, moved);
          return items;
        });
      }
    });

    return () => {
      sortable.destroy();
      setItemsRef.current([]);
    };
  }, []);

  const isPdf = props.kind === "pdf";
  const dataCy = DATA_CY[props.kind];
  const totalBytes = props.items.reduce((sum, item) => sum + item.file.size, 0);
  const previewItem = props.items.find((item) => item.id === previewId);

  return (
    <div
      className="inherit-parent-height display-flex flex-direction-column flex-justify-content-space-between overflow-scroll"
      data-cy={dataCy.root}
    >
      <h5 className="font-weight-normal font-family-gilroy-regular font-size-small font-color-secondary inherit-parent-width text-align-center margin-bottom-large">
        {isPdf ? "ADD PDFS" : "ADD PAGES"} (MAX. {MAX_UPLOAD_FILES})
      </h5>

      <ul ref={listRef} className="rearrange-card-list flex-grow-1">
        {props.items.map((item, index) => (
          <li
            key={item.id}
            className="rearrange-card rearrange-card-outline border-radius-default allow-dragging"
            data-cy={`FILE-CARD-${index}`}
          >
            <section
              className="rearrange-card-thumbnail border-radius-default position-relative overflow-hidden"
              onClick={() => setPreviewId(item.id)}
            >
              <img
                alt=""
                data-cy="FILE-CARD-THUMBNAIL"
                className="inherit-parent-height inherit-parent-width object-fit-cover"
                src={item.thumbnail}
              />
              <div
                data-cy="FILE-CARD-DELETE"
                className="rearrange-card-delete position-absolute border-radius-100-percentage flex-place-children-page-center"
                onClick={(event) => {
                  event.stopPropagation();
                  props.setItems(
                    props.items.filter((other) => other.id !== item.id)
                  );
                }}
              >
                <DeleteIcon width="12" height="15" color="white" />
              </div>
            </section>
            <div className="display-flex flex-align-items-center flex-justify-content-space-between padding-top-small">
              <div className="rearrange-card-details">
                <h6
                  data-cy="FILE-CARD-NAME"
                  className="rearrange-card-name font-family-gilroy-medium font-size-small font-color-secondary"
                >
                  {item.file.name}
                </h6>
                <p className="font-family-gilroy-regular font-size-smaller font-color-tertiary">
                  {isPdf
                    ? `${item.pageCount} ${
                        item.pageCount === 1 ? "page" : "pages"
                      }`
                    : `Page ${index + 1}`}
                </p>
              </div>
              <DragIcon />
            </div>
          </li>
        ))}

        {props.items.length < MAX_UPLOAD_FILES && (
          <li
            className="rearrange-card rearrange-add-card rearrange-card-outline border-radius-default"
            data-cy={dataCy.add}
          >
            <section
              className="rearrange-card-thumbnail border-radius-default background-color-grey flex-place-children-page-center"
              onClick={() => props.onAddClick?.()}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M9 1C9 0.447715 8.55229 0 8 0C7.44772 0 7 0.447715 7 1V5.66667C7 6.40305 6.40305 7 5.66667 7H1C0.447715 7 0 7.44772 0 8C0 8.55229 0.447715 9 1 9H5.66667C6.40305 9 7 9.59695 7 10.3333V15C7 15.5523 7.44772 16 8 16C8.55229 16 9 15.5523 9 15V10.3333C9 9.59695 9.59695 9 10.3333 9H15C15.5523 9 16 8.55229 16 8C16 7.44772 15.5523 7 15 7H10.3333C9.59695 7 9 6.40305 9 5.66667V1Z"
                  fill="#4E4E4E"
                />
              </svg>
            </section>
            {/* invisible copy of a file card's details row, so the add card
                is exactly as tall as the file cards beside it */}
            <div
              aria-hidden="true"
              className="rearrange-add-card-spacer padding-top-small"
            >
              <h6 className="font-family-gilroy-medium font-size-small">
                &nbsp;
              </h6>
              <p className="font-family-gilroy-regular font-size-smaller">
                &nbsp;
              </p>
            </div>
          </li>
        )}
      </ul>

      <div>
        <h6
          data-cy="REARRANGE-FOOTER-NOTE"
          className="font-family-gilroy-regular font-size-small font-color-secondary inherit-parent-width text-align-center"
        >
          {isPdf
            ? `${(totalBytes / 1_000_000).toFixed(1)} MB of ${
                MAX_PDF_TOTAL_BYTES / 1_000_000
              } MB used · Drag and drop to rearrange`
            : "Drag and drop to rearrange the pages"}
        </h6>
        <Button
          className="margin-top-medium"
          text={props.uploadButtonText || "Upload"}
          loading={props.isLoading}
          disabled={props.items.length === 0}
          onClick={() => props.onUploadClick?.()}
          data-cy={dataCy.upload}
          buttonKey={dataCy.upload}
          boxShadow={false}
        />
      </div>

      <PreviewModal
        item={previewItem}
        isPdf={isPdf}
        onClose={() => setPreviewId(null)}
        onDelete={() => {
          props.setItems(props.items.filter((item) => item.id !== previewId));
          setPreviewId(null);
        }}
      />
    </div>
  );
}

function DragIcon() {
  return (
    <svg
      data-cy="FILE-CARD-DRAG-ICON"
      className="rearrange-card-drag-icon"
      width="12"
      height="18"
      viewBox="0 0 12 18"
      fill="#757575"
    >
      <circle cx="3" cy="3" r="1.5" />
      <circle cx="9" cy="3" r="1.5" />
      <circle cx="3" cy="9" r="1.5" />
      <circle cx="9" cy="9" r="1.5" />
      <circle cx="3" cy="15" r="1.5" />
      <circle cx="9" cy="15" r="1.5" />
    </svg>
  );
}

function PreviewModal(props) {
  // tagged with the item id so a stale buffer is never shown for another PDF
  const [loaded, setLoaded] = useState({ id: null, buffer: null });
  const arrayBuffer = loaded.id === props.item?.id ? loaded.buffer : null;

  useEffect(() => {
    if (!props.item || !props.isPdf) return;
    const { id, file } = props.item;
    let cancelled = false;
    file.arrayBuffer().then((buffer) => {
      if (!cancelled) setLoaded({ id, buffer });
    });
    return () => {
      cancelled = true;
    };
  }, [props.item, props.isPdf]);

  return (
    <Modal
      show={!!props.item}
      onClose={props.onClose}
      position="position-fixed top-0 right-0 bottom-0 left-0"
      boxShadow="none"
      borderRadius="none"
      width="inherit-parent-width"
      height="inherit-parent-height"
      maxHeight="inherit-parent-height"
      background="background-white"
    >
      <div
        data-cy="FILE-PREVIEW-MODAL"
        className="inherit-parent-height inherit-parent-width display-flex flex-direction-column"
      >
        <header className="padding-larger display-flex flex-align-items-center flex-justify-content-space-between">
          <span className="rearrange-card-name font-family-gilroy-medium font-size-medium">
            {props.item?.file.name}
          </span>
          <div className="display-flex flex-align-items-center">
            <button
              type="button"
              data-cy="FILE-PREVIEW-DELETE"
              className="background-transparent padding-small margin-right-medium"
              onClick={props.onDelete}
            >
              <DeleteIcon width="20" height="20" />
            </button>
            <button
              type="button"
              data-cy="FILE-PREVIEW-CLOSE"
              className="background-transparent padding-small"
              onClick={props.onClose}
            >
              <CloseIcon />
            </button>
          </div>
        </header>
        <div className="flex-grow-1 overflow-auto">
          {props.isPdf
            ? arrayBuffer && (
                <PDFRenderer readOnly={true} pdfArrayBuffer={arrayBuffer} />
              )
            : props.item && (
                <img
                  alt=""
                  data-cy="FILE-PREVIEW-IMAGE"
                  className="inherit-parent-width"
                  src={props.item.thumbnail}
                />
              )}
        </div>
      </div>
    </Modal>
  );
}

ReArrangeFiles.propTypes = {
  kind: PropTypes.oneOf(["image", "pdf"]).isRequired,
  items: PropTypes.array.isRequired,
  // a useState setter: reordering passes it an updater function
  setItems: PropTypes.func.isRequired,
  onAddClick: PropTypes.func,
  onUploadClick: PropTypes.func,
  uploadButtonText: PropTypes.string,
  isLoading: PropTypes.bool
};
